export type TotalsSource =
  | { kind: 'header'; field: string }
  | { kind: 'tabular'; part: string; field: string; agg: 'sum' }

export interface TotalsItem {
  id: string
  visible: boolean
  order: number
  source: TotalsSource
}

export const TOTALS_NAME_RE = /total|sum|amount|vat|tax|discount|qty/i

function isNumberType(attrType: string): boolean {
  return attrType === 'number'
}

function headerMatches(name: string, synonym: string): boolean {
  return TOTALS_NAME_RE.test(name) || TOTALS_NAME_RE.test(synonym)
}

export function collectTotalsCandidates(
  headerAttrs: { name: string; synonym: string; attr_type: string }[],
  tabularParts: { name: string; columns: { name: string; attr_type: string }[] }[],
): TotalsItem[] {
  const items: TotalsItem[] = []
  for (const attr of headerAttrs) {
    if (!isNumberType(attr.attr_type) || !headerMatches(attr.name, attr.synonym)) continue
    items.push({
      id: `header:${attr.name}`,
      visible: false,
      order: items.length,
      source: { kind: 'header', field: attr.name },
    })
  }
  for (const part of tabularParts) {
    for (const col of part.columns) {
      if (!isNumberType(col.attr_type)) continue
      items.push({
        id: `tabular:${part.name}:${col.name}`,
        visible: false,
        order: items.length,
        source: { kind: 'tabular', part: part.name, field: col.name, agg: 'sum' },
      })
    }
  }
  return items
}

function toFiniteNumber(value: unknown): number | null {
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

export function computeTotalsValue(
  item: TotalsItem,
  formValues: Record<string, unknown>,
): number | null {
  if (item.source.kind === 'header') {
    return toFiniteNumber(formValues[item.source.field])
  }
  const rows = formValues[item.source.part]
  if (!Array.isArray(rows)) return null
  let sum = 0
  let anyFinite = false
  for (const row of rows) {
    if (row == null || typeof row !== 'object') continue
    const n = toFiniteNumber((row as Record<string, unknown>)[item.source.field])
    if (n == null) continue
    sum += n
    anyFinite = true
  }
  return anyFinite ? sum : null
}

export function totalsItemLabel(item: TotalsItem): string {
  if (item.source.kind === 'header') return item.source.field
  return `${item.source.part}.${item.source.field}`
}

export function sameTotalsSource(a: TotalsSource, b: TotalsSource): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'header' && b.kind === 'header') return a.field === b.field
  if (a.kind === 'tabular' && b.kind === 'tabular') {
    return a.part === b.part && a.field === b.field && a.agg === b.agg
  }
  return false
}
